import { TrackEventInput } from '@updater/ui-tracker';
import { DOMAIN } from 'flows/forward-mail/constants/analytics-tracker';
import { useRouter } from 'next/router';
import { useEffect, useMemo, useState } from 'react';
import { useTracking } from 'react-tracking';
import { Item } from 'types/item';
import { useSWRWhenAuthed } from 'utils/use-swr-when-authed';
import { useUserData } from './use-profile';

import { FORWARD_MAIL_FORMS_SUBMITED } from 'flows/forward-mail/constants';

/* Checks if user is an uninvited user (mostly mobile) */
export const useIsUninvited = () => {
  const { loading: userDataLoading, user } = useUserData();

  const v2UserData = useSWRWhenAuthed(`/v2/users/${user?.id}`);

  return {
    loading: userDataLoading || v2UserData?.isLoading,
    didUserClaimInvite: v2UserData?.data?.claimed_invite_id !== null,
  };
};

export type UseIsUninvitedResult = ReturnType<typeof useIsUninvited>;

export const useIsMoveAgain = () => {
  const { loading: userDataLoading, user } = useUserData();

  const v2UserData = useSWRWhenAuthed(`/v2/users/${user?.id}`);

  return {
    loading: userDataLoading || v2UserData?.isLoading,
    moveCounts: v2UserData?.data?.moves?.length ?? 0,
    moveAgain: v2UserData?.data?.moves?.length > 1 ?? false,
  };
};

/**
 * Returns total fym request count.
 *
 * How - check /v2/items & find all `forward_mail` w/ status
 * of `completed` | `validating`.
 */
export const useFYMCount = () => {
  const { data: items, isLoading } = useSWRWhenAuthed<Item[]>('/v2/items');

  const forwardMailItems = items?.filter(
    ({ kind, status }) =>
      kind === 'forward_mail' && status !== 'cancelled'
  );

  return {
    forwardMailRequestCount: forwardMailItems?.length ?? 0,
    loading: isLoading,
  };
};

export const useFYMInVerificationMode = () => {
  const { data: items, isLoading } = useSWRWhenAuthed<Item[]>('/v2/items');
  // if user has completed at least one request but the move addressees are not verified
  const inVerificationMode = Boolean(
    items?.find(
      ({ kind, status, move }) =>
        kind === 'forward_mail' && status === 'unverified' && move?.need_verify
    )
  );
  return {
    inVerificationMode,
    loading: isLoading,
  };
};

export type UseFYMCountResult = ReturnType<typeof useFYMCount>;

/**
 * Wrapper function to return the user type data.
 *
 * Checks if user is a "move again" type user.
 * Business logic from [this ticket](https://app.clickup.com/t/862j93t6r).
 */
export const useFYMStatus = () => {
  const isUninvitedData = useIsUninvited();
  const fymCountData = useFYMCount();
  const moveAgainData = useIsMoveAgain();
  // if user has completed at least one request but the move addressees are not verified
  const InVerificationModeData = useFYMInVerificationMode();

  /**
   * Less than 2 forward mail requests || user did not claim invite.
   * Business logic from [this ticket](https://app.clickup.com/t/862j93t6r).
   */
  const showFYM = useMemo(() => {
    const hasLessThan2FymRequest = fymCountData?.forwardMailRequestCount < 2;
    if (isUninvitedData?.didUserClaimInvite && hasLessThan2FymRequest) {
      return true;
    }

    return false;
  }, [
    fymCountData?.forwardMailRequestCount,
    isUninvitedData?.didUserClaimInvite,
  ]);

  return {
    showFYM,
    forwardMailRequestCount: fymCountData?.forwardMailRequestCount,
    inVerificationMode: InVerificationModeData?.inVerificationMode,
    didUserClaimInvite: isUninvitedData?.didUserClaimInvite,
    moveAgainData,
    loading:
      isUninvitedData?.loading ||
      fymCountData?.loading ||
      moveAgainData?.loading ||
      InVerificationModeData?.loading,
  };
};

export type UseFYMStatusResult = ReturnType<typeof useFYMStatus>;

/**
 * Automatically redirect user to `/forward-mail` if not invited | more than 2 fym requests | move again.
 * Ticket info here - https://app.clickup.com/t/862ja6qpv
 */
export const useFYMRouteGuard = () => {
  const {
    loading,
    didUserClaimInvite,
    forwardMailRequestCount,
    moveAgainData,
  } = useFYMStatus();
  const { moveAgain } = moveAgainData;
  const router = useRouter();
  const [isAllowedToProceed, setIsAllowedToProceed] = useState(false);

  const { trackEvent } = useTracking<TrackEventInput<unknown>>({
    domain: DOMAIN.FORWARD_MAIL,
  });

  useEffect(() => {
    if (loading) return;

    const isInAllowedRoute =
      router.asPath === '/forward-mail' ||
      router.asPath.includes('receipts') ||
      router.asPath === FORWARD_MAIL_FORMS_SUBMITED;

    // for users who did not claim invite, or user already has 2 requests, or moving again,
    // block user from fym flow and send back to entry/vertical details
    if (
      !isInAllowedRoute &&
      (!didUserClaimInvite || forwardMailRequestCount >= 2 || moveAgain)
    ) {
      trackEvent({
        object: 'forward-mail-routing',
        details: {
          isInAllowedRoute,
          moveAgain,
          invitedUser: didUserClaimInvite,
          fymRequestCount: forwardMailRequestCount,
        },
        verb: 'tried',
      });

      router.push('/forward-mail');
      return;
    }

    setIsAllowedToProceed(true);
  }, [
    router,
    loading,
    didUserClaimInvite,
    forwardMailRequestCount,
    moveAgainData,
    trackEvent,
    moveAgain,
  ]);

  return { loading, isAllowedToProceed };
};
